// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/EnterpriseAdminPage.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAdvisor } from "../../AdvisorContext";
import EnterpriseAdminNavigation from "./EnterpriseAdminNavigation";
import AdministrationOverview from "./AdministrationOverview";
import OrganizationProfilePanel from "./organization/OrganizationProfilePanel";
import OrganizationPreferencesPanel from "./organization/OrganizationPreferencesPanel";
import GovernanceAuditPanel from "./audit/GovernanceAuditPanel";
import UserManagementPanel from "./users/UserManagementPanel";
import RoleManagementPanel from "./roles/RoleManagementPanel";
import ConnectorGovernancePanel from "./connectors/ConnectorGovernancePanel";
import LifecycleWorkbench from "./lifecycle/LifecycleWorkbench";
import CommercialAdministrationPanel from "./commercial/CommercialAdministrationPanel";
import "./EnterpriseAdmin.css";

const MIN_READINESS_REFRESH_MS = 800;
const MIN_CONNECTOR_REFRESH_MS = 800;

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function buildHeaders(token, tenantId, userId, userName) {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    "X-Tenant-Id": tenantId,
    "X-User-Id": userId,
    "X-User-Name": userName || "OASYS Administrator",
  };
}

async function parseJson(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}

function normalizeConnectorOperation(connector = {}) {
  const owner = connector.owner || {};
  const verifiedAccount = connector.verified_account || {};
  const lifecycleState = connector.lifecycle_state || "not_configured";
  const disconnected = lifecycleState === "disconnected";

  return {
    ...connector,
    owner_user_id: owner.user_id || null,
    owner_name: owner.name || null,
    owner_email: owner.email || null,
    connection: {
      status: disconnected ? "disconnected" : connector.authorization_status,
      last_health_status: connector.health_status || "not_checked",
      verified_account_reference: verifiedAccount.reference || null,
      verified_account_name: verifiedAccount.name || null,
      verified_at: connector.last_verified_at || null,
      last_health_check_at: connector.last_health_check_at || null,
    },
    metadata: {
      verified_account_reference: verifiedAccount.reference || null,
      verified_account_name: verifiedAccount.name || null,
    },
  };
}

function normalizeConnectorOperations(items) {
  return (Array.isArray(items) ? items : []).map(normalizeConnectorOperation);
}

function normalizeInitialSection(routeParams) {
  const raw = String(routeParams?.section || routeParams?.adminSection || "overview")
    .trim()
    .toLowerCase();

  if (["roles", "permissions", "roles-permissions"].includes(raw)) return "roles";
  if (["user", "users", "invitations"].includes(raw)) return "users";
  if (["connector", "connectors", "connector-governance"].includes(raw)) return "connectors";
  if (["lifecycle", "offboarding", "retention", "offboarding-retention"].includes(raw)) return "lifecycle";
  if (["commercial", "agreement", "pricing", "billing"].includes(raw)) return "commercial";
  if (["overview", "organization", "preferences", "audit"].includes(raw)) return raw;
  return "overview";
}

export default function EnterpriseAdminPage({
  apiBaseUrl,
  tenantId,
  userId,
  userProfile,
  getAccessToken,
  routeParams = {},
  onModeChange,
  themeMode = "dark",
}) {
  const { setCopilotContext, clearCopilotContext } = useAdvisor();
  const resolvedThemeMode =
    String(themeMode || "").trim().toLowerCase() === "light" ? "light" : "dark";
  const pageThemeClass =
    resolvedThemeMode === "light" ? "oasys-theme-light" : "oasys-theme-dark";
  const [activeSection, setActiveSection] = useState(() => normalizeInitialSection(routeParams));
  const [overview, setOverview] = useState(null);
  const [profile, setProfile] = useState(null);
  const [organizationHistory, setOrganizationHistory] = useState([]);
  const [organizationNotice, setOrganizationNotice] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [audit, setAudit] = useState(null);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState({});
  const [currentPermissions, setCurrentPermissions] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [connectorSummary, setConnectorSummary] = useState(null);
  const [connectorAudit, setConnectorAudit] = useState([]);
  const [governanceEvents, setGovernanceEvents] = useState([]);
  const [governanceSummary, setGovernanceSummary] = useState(null);
  const [governanceCategories, setGovernanceCategories] = useState([]);
  const [governanceExport, setGovernanceExport] = useState(null);
  const [tenantReadiness, setTenantReadiness] = useState(null);
  const [readinessRefreshedAt, setReadinessRefreshedAt] = useState(null);
  const [connectorsRefreshedAt, setConnectorsRefreshedAt] = useState(null);
  const [commercialAccount, setCommercialAccount] = useState(null);
  const [commercialPricing, setCommercialPricing] = useState(null);
  const [commercialHistory, setCommercialHistory] = useState(null);
  const [commercialLoading, setCommercialLoading] = useState(false);
  const [commercialError, setCommercialError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const userName = useMemo(
    () => userProfile?.name || userProfile?.email || "OASYS Administrator",
    [userProfile]
  );
  const canReadCommercial =
    Array.isArray(currentPermissions) && currentPermissions.includes("commercial.read");
  const canRequestCommercial =
    Array.isArray(currentPermissions) && currentPermissions.includes("commercial.request");

  const routeSection = routeParams?.section;
  const routeAdminSection = routeParams?.adminSection;

  useEffect(() => {
    clearCopilotContext();
    setCopilotContext({
      mode: "admin",
      page: "enterprise_admin",
      surface: "enterprise_admin_page",
      intelligence_mode: "page",
      section: activeSection || "overview",
    });
  }, [activeSection, clearCopilotContext, setCopilotContext]);
  const launchedFromMission = routeParams?.returnTo === "mission-launch";
  const returnStep = routeParams?.returnStep || "connectors";

  const returnToMissionLaunch = useCallback(() => {
    if (!launchedFromMission || typeof onModeChange !== "function") return;

    onModeChange("mission-launch", {
      step: returnStep,
      connectorRefresh: String(Date.now()),
    });
  }, [launchedFromMission, onModeChange, returnStep]);

  useEffect(() => {
    const requestedSection = normalizeInitialSection({
      section: routeSection,
      adminSection: routeAdminSection,
    });

    if (
      requestedSection === "commercial" &&
      currentPermissions !== null &&
      !canReadCommercial
    ) {
      setActiveSection("overview");
      return;
    }

    setActiveSection(requestedSection);
  }, [routeSection, routeAdminSection, currentPermissions, canReadCommercial]);

  const request = useCallback(
    async (path, options = {}) => {
      const token = await getAccessToken();
      if (!token) throw new Error("Authentication token unavailable.");

      const response = await fetch(`${apiBaseUrl}${path}`, {
        ...options,
        headers: {
          ...buildHeaders(token, tenantId, userId, userName),
          ...(options.headers || {}),
        },
      });

      const data = await parseJson(response);
      if (!response.ok) {
        const detail = data?.detail;
        throw new Error(
          typeof detail === "string"
            ? detail
            : detail
              ? JSON.stringify(detail)
              : `Request failed with HTTP ${response.status}`
        );
      }
      return data;
    },
    [apiBaseUrl, getAccessToken, tenantId, userId, userName]
  );

  const loadAudit = useCallback(async () => {
    const [auditData, governanceData, governanceSummaryData, governanceCategoryData] = await Promise.all([
      request("/enterprise-admin/audit?limit=25"),
      request("/enterprise-admin/governance-events?limit=50"),
      request("/enterprise-admin/governance-events/summary"),
      request("/enterprise-admin/governance-events/categories"),
    ]);

    setAudit(auditData);
    setGovernanceEvents(governanceData?.items || []);
    setGovernanceSummary(governanceSummaryData);
    setGovernanceCategories(governanceCategoryData?.items || []);
    return { auditData, governanceData, governanceSummaryData, governanceCategoryData };
  }, [request]);

  const loadConnectorState = useCallback(async () => {
    const [connectorData, connectorAuditData] = await Promise.all([
      request("/enterprise-admin/connectors/operations"),
      request("/enterprise-admin/connectors/audit?limit=100"),
    ]);

    setConnectors(normalizeConnectorOperations(connectorData?.items));
    setConnectorSummary(connectorData?.summary || null);
    setConnectorAudit(connectorAuditData?.items || []);
    return { connectorData, connectorAuditData };
  }, [request]);

  const loadUserState = useCallback(
    async (includeRemoved = false) => {
      const query = includeRemoved ? "?include_removed=true" : "";
      const userData = await request(`/enterprise-admin/users${query}`);
      setUsers(userData?.items || []);
      setInvitations(userData?.invitations || []);
      return userData;
    },
    [request]
  );

  const loadCommercialState = useCallback(async ({ showLoading = true } = {}) => {
    setCommercialError(null);
    if (showLoading) setCommercialLoading(true);
    try {
      const [accountData, pricingData, historyData] = await Promise.all([
        request("/enterprise-admin/commercial"),
        request("/enterprise-admin/commercial/pricing"),
        request("/enterprise-admin/commercial/history?limit=100"),
      ]);
      setCommercialAccount(accountData);
      setCommercialPricing(pricingData);
      setCommercialHistory(historyData);
      return { accountData, pricingData, historyData };
    } catch (err) {
      const message = String(err?.message || err);
      setCommercialError(message);
      throw err;
    } finally {
      if (showLoading) setCommercialLoading(false);
    }
  }, [request]);

  const submitCommercialPlanRequest = useCallback(
    async ({
      requestedPlan,
      contactName,
      workEmail,
      message,
      idempotencyKey,
    }) => {
      if (!requestedPlan?.id) {
        throw new Error("Requested OASYS plan is unavailable.");
      }

      return request("/enterprise-admin/commercial/plan-requests", {
        method: "POST",
        body: JSON.stringify({
          requested_package_version_id: requestedPlan.id,
          contact_name: contactName,
          work_email: workEmail,
          message: message || null,
          idempotency_key: idempotencyKey,
        }),
      });
    },
    [request]
  );

  const loadAll = useCallback(async () => {
    if (!tenantId || !userId) {
      setError("Administration requires tenant and user context.");
      setLoading(false);
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const [
        contextData,
        overviewData,
        profileData,
        organizationHistoryData,
        preferenceData,
        auditData,
        userData,
        roleData,
        permissionData,
        connectorData,
        connectorAuditData,
        governanceData,
        governanceSummaryData,
        governanceCategoryData,
        readinessData,
      ] = await Promise.all([
        request("/enterprise-admin/context"),
        request("/enterprise-admin/overview"),
        request("/enterprise-admin/organization"),
        request("/enterprise-admin/organization/history?limit=25"),
        request("/enterprise-admin/preferences"),
        request("/enterprise-admin/audit?limit=25"),
        request("/enterprise-admin/users"),
        request("/enterprise-admin/roles"),
        request("/enterprise-admin/permissions"),
        request("/enterprise-admin/connectors/operations"),
        request("/enterprise-admin/connectors/audit?limit=100"),
        request("/enterprise-admin/governance-events?limit=50"),
        request("/enterprise-admin/governance-events/summary"),
        request("/enterprise-admin/governance-events/categories"),
        request("/enterprise-admin/readiness"),
      ]);

      setOverview(overviewData);
      setProfile(profileData);
      setOrganizationHistory(organizationHistoryData?.items || []);
      setPreferences(preferenceData);
      setAudit(auditData);
      setUsers(userData?.items || []);
      setInvitations(userData?.invitations || []);
      setRoles(roleData?.items || []);
      setPermissionGroups(permissionData?.groups || {});
      setCurrentPermissions(
        Array.isArray(contextData?.permissions) ? contextData.permissions : []
      );
      setConnectors(normalizeConnectorOperations(connectorData?.items));
      setConnectorSummary(connectorData?.summary || null);
      setConnectorAudit(connectorAuditData?.items || []);
      setGovernanceEvents(governanceData?.items || []);
      setGovernanceSummary(governanceSummaryData || null);
      setGovernanceCategories(governanceCategoryData?.items || []);
      setTenantReadiness(readinessData || overviewData?.readiness || null);
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  }, [request, tenantId, userId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (activeSection !== "commercial" || !canReadCommercial) return;
    loadCommercialState().catch(() => {
      // Commercial read authority surfaces its own error state.
    });
  }, [activeSection, canReadCommercial, loadCommercialState]);

  const runAdminAction = async (action, options = {}) => {
    const shouldReload = options.reload !== false;
    const surfaceErrorGlobally = options.surfaceError !== false;
    setSaving(true);

    if (surfaceErrorGlobally) setError(null);

    try {
      const result = await action();
      if (shouldReload) await loadAll();
      return result;
    } catch (err) {
      if (surfaceErrorGlobally) setError(String(err?.message || err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const saveOrganization = async (updates) => {
    setSaving(true);
    setError(null);
    setOrganizationNotice(null);
    try {
      const updated = await request("/enterprise-admin/organization", {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      const historyData = await request("/enterprise-admin/organization/history?limit=25");
      setProfile(updated);
      setOrganizationHistory(historyData?.items || []);
      setOrganizationNotice({
        type: "success",
        message: "Organization profile saved and added to the administrative audit history.",
      });
      await loadAudit();
      return updated;
    } catch (err) {
      const message = String(err?.message || err);
      setError(message);
      setOrganizationNotice({ type: "error", message });
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const savePreferences = async (updates) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await request("/enterprise-admin/preferences", {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      setPreferences(updated);
      await loadAudit();
      return updated;
    } catch (err) {
      setError(String(err?.message || err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const adminActions = {
    listUsers: (includeRemoved = false) => loadUserState(includeRemoved),
    invite: async (payload) => {
      const created = await request("/enterprise-admin/users/invite", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setInvitations((current) => [created, ...current.filter((item) => item.id !== created.id)]);
      return created;
    },
    resendInvitation: async (invitationId) => {
      const updated = await request(`/enterprise-admin/invitations/${invitationId}/resend`, { method: "POST" });
      setInvitations((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      return updated;
    },
    cancelInvitation: async (invitationId) => {
      const updated = await request(`/enterprise-admin/invitations/${invitationId}/cancel`, { method: "POST" });
      setInvitations((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      return updated;
    },
    assignRole: (targetUserId, roleKey) => runAdminAction(
      () => request(`/enterprise-admin/users/${targetUserId}/roles`, {
        method: "POST",
        body: JSON.stringify({ role_key: roleKey }),
      }),
      { surfaceError: false }
    ),
    removeRole: (targetUserId, roleKey) => runAdminAction(
      () => request(`/enterprise-admin/users/${targetUserId}/roles/${roleKey}`, { method: "DELETE" }),
      { surfaceError: false }
    ),
    suspendUser: (targetUserId, reason = "Suspended from Enterprise Administration") => runAdminAction(
      () => request(`/enterprise-admin/users/${targetUserId}/suspend`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
      { surfaceError: false }
    ),
    reactivateUser: (targetUserId, reason = "Reactivated from Enterprise Administration") => runAdminAction(
      () => request(`/enterprise-admin/users/${targetUserId}/reactivate`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
      { surfaceError: false }
    ),
    removeUser: (targetUserId, reason) => runAdminAction(
      () => request(`/enterprise-admin/users/${targetUserId}`, {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      }),
      { surfaceError: false }
    ),
    getUserDetail: (targetUserId) => request(`/enterprise-admin/users/${targetUserId}`),
    updateConnectorGovernance: (connectorId, payload) => runAdminAction(() => request(`/enterprise-admin/connectors/${connectorId}/governance`, { method: "PATCH", body: JSON.stringify(payload) })),
    markConnectorOwner: (connectorId, payload) => runAdminAction(() => request(`/enterprise-admin/connectors/${connectorId}/mark-owner`, { method: "POST", body: JSON.stringify(payload) })),
    requestConnectorReauthorization: (connectorId, reason) => runAdminAction(() => request(`/enterprise-admin/connectors/${connectorId}/request-reauthorization`, { method: "POST", body: JSON.stringify({ reason }) })),
    pauseConnector: (connectorId, reason) => runAdminAction(() => request(`/enterprise-admin/connectors/${connectorId}/pause`, { method: "POST", body: JSON.stringify({ reason }) })),
    resumeConnector: (connectorId, reason) => runAdminAction(() => request(`/enterprise-admin/connectors/${connectorId}/resume`, { method: "POST", body: JSON.stringify({ reason }) })),
    getConnectorDetail: (connectorId) => request(`/enterprise-admin/connectors/${connectorId}`),
    getConnectorHealth: (connectorId) => runAdminAction(() => request(`/enterprise-admin/connectors/${connectorId}/health`), { reload: false }),
    reverifyConnector: (connectorId, payload = {}) => runAdminAction(() => request(`/enterprise-admin/connectors/${connectorId}/reverify`, { method: "POST", body: JSON.stringify(payload || {}) }), { reload: false }),
    disconnectConnector: (connectorId, payload = {}) => runAdminAction(() => request(`/enterprise-admin/connectors/${connectorId}/disconnect`, { method: "POST", body: JSON.stringify(payload || {}) }), { reload: false }),
    reconnectConnector: (connectorId, payload = {}) => runAdminAction(() => request(`/enterprise-admin/connectors/${connectorId}/reconnect`, { method: "POST", body: JSON.stringify(payload || {}) }), { reload: false }),
    refreshConnectors: () => runAdminAction(async () => {
      const startedAt = Date.now();
      const result = await loadConnectorState();
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_CONNECTOR_REFRESH_MS) await sleep(MIN_CONNECTOR_REFRESH_MS - elapsed);
      setConnectorsRefreshedAt(new Date().toISOString());
      return result;
    }, { reload: false }),
    getConnectorSetupCatalog: () => request("/enterprise-admin/connectors/setup/catalog"),
    getConnectorSetupDetail: (providerKey) => request(`/enterprise-admin/connectors/setup/${providerKey}`),
    startConnectorSetup: (providerKey, payload) => runAdminAction(() => request(`/enterprise-admin/connectors/setup/${providerKey}/start`, { method: "POST", body: JSON.stringify(payload || {}) }), { reload: false }),
    validateConnectorSetup: (providerKey, payload) => runAdminAction(() => request(`/enterprise-admin/connectors/setup/${providerKey}/validate`, { method: "POST", body: JSON.stringify({ setup_inputs: payload || {} }) }), { reload: false }),
    completeConnectorSetup: (providerKey, payload) => runAdminAction(() => request(`/enterprise-admin/connectors/setup/${providerKey}/complete`, { method: "POST", body: JSON.stringify(payload || {}) }), { reload: false }),
    submitConnectorRequest: (payload) => runAdminAction(() => request("/enterprise-admin/connectors/setup/requests", { method: "POST", body: JSON.stringify(payload || {}) }), { reload: false }),
    getConnectorRequests: (limit = 100) => request(`/enterprise-admin/connectors/setup/requests?limit=${limit}`),
    getConnectorRequestDetail: (requestId) => request(`/enterprise-admin/connectors/setup/requests/${requestId}`),
    refreshGovernanceEvents: () => loadAudit(),
    refreshTenantReadiness: () => runAdminAction(async () => {
      const startedAt = Date.now();
      const result = await request("/enterprise-admin/readiness/refresh", { method: "POST" });
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_READINESS_REFRESH_MS) await sleep(MIN_READINESS_REFRESH_MS - elapsed);
      setTenantReadiness(result);
      setReadinessRefreshedAt(new Date().toISOString());
      setOverview((current) => ({
        ...(current || {}),
        administration_readiness: result?.score ?? current?.administration_readiness,
        mvp_gate: result?.mvp_gate || current?.mvp_gate,
        readiness: result,
      }));
      return result;
    }, { reload: false }),
    exportGovernanceEvents: (payload) => runAdminAction(async () => {
      const result = await request("/enterprise-admin/governance-events/export", {
        method: "POST",
        body: JSON.stringify(payload || { export_format: "json", limit: 250 }),
      });
      setGovernanceExport(result);
      return result;
    }),
  };

  const renderSection = () => {
    if (loading) return <div className="enterprise-admin-loading">Loading Administration…</div>;

    if (error) {
      return (
        <div className="enterprise-admin-error">
          <h3>Administration unavailable</h3>
          <p>{error}</p>
          <button type="button" onClick={loadAll}>Retry</button>
        </div>
      );
    }

    if (activeSection === "organization") {
      return <OrganizationProfilePanel profile={profile} history={organizationHistory} notice={organizationNotice} onDismissNotice={() => setOrganizationNotice(null)} onSave={saveOrganization} saving={saving} />;
    }

    if (activeSection === "users") {
      return <UserManagementPanel users={users} invitations={invitations} roles={roles} currentUserId={userId} onRefresh={loadAll} actions={adminActions} saving={saving} />;
    }

    if (activeSection === "roles") {
      return <RoleManagementPanel roles={roles} permissions={permissionGroups} saving={saving} />;
    }

    if (activeSection === "connectors") {
      return (
        <div>
          <ConnectorGovernancePanel
          captureApiBaseUrl={apiBaseUrl}
          captureGetAccessToken={getAccessToken}
          captureTenantId={tenantId}
          captureUserId={userId}
          connectors={connectors}
          summary={connectorSummary}
          audit={connectorAudit}
          users={users}
          actions={adminActions}
          saving={saving}
          onRefresh={adminActions.refreshConnectors}
          refreshedAt={connectorsRefreshedAt}
          initialSetupProviderKey={routeParams?.provider || null}
          setupRequested={String(routeParams?.setup || "") === "1"}
          returnTo={routeParams?.returnTo || null}
          returnStep={routeParams?.returnStep || "connectors"}
          onReturn={(mode, params) => onModeChange?.(mode, params)}
          />
        </div>
      );
    }

    if (activeSection === "commercial") {
      if (!canReadCommercial) {
        return (
          <AdministrationOverview
            overview={overview}
            readiness={tenantReadiness || overview?.readiness}
            actions={adminActions}
            saving={saving}
            readinessRefreshedAt={readinessRefreshedAt}
          />
        );
      }

      return (
        <CommercialAdministrationPanel
          accountData={commercialAccount}
          pricingData={commercialPricing}
          historyData={commercialHistory}
          loading={commercialLoading}
          error={commercialError}
          onRetry={() => loadCommercialState().catch(() => {})}
          onUpgradeRequest={canRequestCommercial ? submitCommercialPlanRequest : undefined}
          organizationName={
            commercialAccount?.account?.legal_name ||
            profile?.legal_name ||
            profile?.organization_name ||
            ""
          }
          requestContactName={userProfile?.name || ""}
          requestContactEmail={
            userProfile?.email ||
            commercialAccount?.account?.billing_email ||
            ""
          }
        />
      );
    }

    if (activeSection === "lifecycle") {
      return <LifecycleWorkbench request={request} saving={saving} themeMode={resolvedThemeMode} />;
    }

    if (activeSection === "preferences") {
      return <OrganizationPreferencesPanel preferences={preferences} onSave={savePreferences} saving={saving} />;
    }

    if (activeSection === "audit") {
      return <GovernanceAuditPanel audit={audit} events={governanceEvents} summary={governanceSummary} categories={governanceCategories} latestExport={governanceExport} actions={adminActions} saving={saving} />;
    }

    return <AdministrationOverview overview={overview} readiness={tenantReadiness || overview?.readiness} actions={adminActions} saving={saving} readinessRefreshedAt={readinessRefreshedAt} />;
  };

  return (
    <div className={`enterprise-admin-page ${pageThemeClass}`}>
      {launchedFromMission ? (
        <section className="enterprise-admin-mission-return-banner" aria-label="Mission Launch connector setup context">
          <div>
            <p className="enterprise-admin-eyebrow">Mission Launch in progress</p>
            <strong>Manage the connections for your current launch plan.</strong>
            <span>Connector status will refresh when you return to the Connectors step.</span>
          </div>
          <button type="button" onClick={returnToMissionLaunch}>Return to Mission Launch</button>
        </section>
      ) : null}

      <section className="enterprise-admin-hero">
        <div>
          <p className="enterprise-admin-eyebrow">Enterprise Administration</p>
          <h1>Command Structure</h1>
          <p>
            Govern organization settings, users, roles, permissions, operating preferences,
            connector authorization, and compliance-ready audit traceability from a secure enterprise control plane.
          </p>
        </div>
      </section>

      <div className="enterprise-admin-layout">
        <EnterpriseAdminNavigation
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          onChange={setActiveSection}
          permissions={currentPermissions || []}
        />
        <main className="enterprise-admin-content">{renderSection()}</main>
      </div>
    </div>
  );
}
